/* eslint-disable react/no-array-index-key */
import {FC, useContext, useMemo} from 'react';
import clsx from 'clsx';
import {
    addDays,
    differenceInDays,
    endOfMonth,
    endOfWeek,
    format,
    isSameDay,
    isSameMonth,
    set,
    startOfMonth,
    startOfWeek,
} from 'date-fns';
import {ja} from 'date-fns/locale';
import {toZonedTime} from 'date-fns-tz';
import {chunk} from 'lodash';
import {useLocale} from 'state/locale';
import {JST_TIMEZONE} from 'utils/date';
import DateSelectContext from '../../context';
import {isSameDate, isValidDate} from '../../utils';
import DateSelectDate from './DateSelectDate';
import {table} from './styles.module.css';

const DateSelectCalendar: FC = () => {
    const {
        currentFocus,
        maxDate,
        minDate,
        onChangeZonedAtJst,
        setCurrentFocus,
        valueZonedAtJst,
        visibleDateZonedAtJst,
    } = useContext(DateSelectContext);

    const locale = useLocale();

    const [days, weeks] = useMemo(() => {
        // the 7 days of the week (Sun-Sat)
        const options = locale === 'en' ? undefined : {locale: ja};
        const hours = valueZonedAtJst ? valueZonedAtJst.getHours() : 9;
        const minutes = valueZonedAtJst ? valueZonedAtJst.getMinutes() : 0;
        const labels = Array.from({length: 7})
            .fill(set(startOfWeek(visibleDateZonedAtJst), {hours, minutes}))
            .map((dateItem, index) =>
                format(addDays(dateItem as number, index), 'EEEEE', options),
            );
        // first day of current month view
        const start = set(startOfWeek(startOfMonth(visibleDateZonedAtJst)), {
            hours,
            minutes,
        });
        // last day of current month view
        const end = set(endOfWeek(endOfMonth(visibleDateZonedAtJst)), {
            hours,
            minutes,
        });

        const now = new Date();
        const nowZonedAtJst = toZonedTime(now, JST_TIMEZONE);

        // get all days and whether they are within the current month and range
        const dates = Array(differenceInDays(end, start) + 1)
            .fill(start)
            .map((s, index) => {
                const date = addDays(s, index);
                const isThisMonth = isSameMonth(visibleDateZonedAtJst, date);
                // if not in range, no click action
                // if in this month, select the date
                // if out of this month, jump to the date
                const onClick = isValidDate(date, minDate, maxDate)
                    ? onChangeZonedAtJst
                    : undefined;

                return {
                    date,
                    isThisMonth,
                    isToday: isSameDay(date, nowZonedAtJst),
                    onClick,
                    selected:
                        valueZonedAtJst && isSameDate(valueZonedAtJst, date),
                };
            });

        return [labels, chunk(dates, 7)];
    }, [
        locale,
        maxDate,
        minDate,
        onChangeZonedAtJst,
        valueZonedAtJst,
        visibleDateZonedAtJst,
    ]);

    return (
        <table className={clsx('w-full', table)}>
            <thead>
                <tr>
                    {days.map((day, dayIndex) => (
                        <th
                            key={`${day}${dayIndex}`}
                            className="w-[14.28%] p-1 text-center text-sm text-light-secondary"
                        >
                            {day}
                        </th>
                    ))}
                </tr>
            </thead>
            <tbody>
                {weeks.map((week, weekIndex) => (
                    <tr key={weekIndex}>
                        {week.map((date, dateIndex) => (
                            <DateSelectDate
                                key={date.date.toString()}
                                cell={weekIndex * 7 + dateIndex}
                                currentFocus={currentFocus}
                                setCurrentFocus={setCurrentFocus}
                                {...date}
                            />
                        ))}
                    </tr>
                ))}
            </tbody>
        </table>
    );
};

export default DateSelectCalendar;
